/* ============================================================================
   ELIO — global accessibility layer (behaviour)
   Loaded once from layout/theme.liquid, deferred.

   Two jobs:
     1. Wire keyboard behaviour into any overlay that appears in the DOM,
        matched by SIGNATURE rather than by a hardcoded class list. A class
        list would silently miss the next drawer built; a signature does not.
     2. Provide one global live region so dynamic changes are announced
        consistently.

   The theme's vendor drawers already inherit focus trap, Escape and focus
   restore from SHTCoreDrawer. This only picks up hand-built overlays, and it
   defers to SHTHelper.trapFocus where available rather than reimplementing it.
   ========================================================================== */
(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]:not([disabled])',
    'button:not([disabled])',
    'summary',
    'textarea:not([disabled])',
    'select:not([disabled])',
    'input:not([type="hidden"]):not([disabled])',
    '[tabindex]:not([tabindex^="-"])'
  ].join(',');

  /* --- live region --------------------------------------------------------- */
  var live = null;

  function liveRegion() {
    if (live && document.body.contains(live)) return live;
    live = document.getElementById('elioA11yLive');
    if (!live) {
      live = document.createElement('div');
      live.id = 'elioA11yLive';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      document.body.appendChild(live);
    }
    return live;
  }

  var announceTimer = null;
  function announce(message) {
    if (!message) return;
    var el = liveRegion();
    clearTimeout(announceTimer);
    // Clearing first forces re-announcement of an identical string.
    el.textContent = '';
    announceTimer = setTimeout(function () { el.textContent = String(message); }, 60);
  }

  /* --- focus management ---------------------------------------------------- */
  function focusables(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll(FOCUSABLE),
      function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      }
    );
  }

  function trap(root) {
    if (window.SHTHelper && typeof window.SHTHelper.trapFocus === 'function') {
      try {
        var first = focusables(root)[0];
        window.SHTHelper.trapFocus(root, first || root);
        return true;
      } catch (e) { /* fall through to local implementation */ }
    }
    return false;
  }

  function release(returnTo) {
    if (window.SHTHelper && typeof window.SHTHelper.removeTrapFocus === 'function') {
      try { window.SHTHelper.removeTrapFocus(returnTo || null); return; } catch (e) {}
    }
    if (returnTo && typeof returnTo.focus === 'function') returnTo.focus();
  }

  /* --- registry ------------------------------------------------------------ */
  var WIRED = 'elioA11yWired';

  /* A modal is anything that declares itself one. The checkout nudge already
     sets role="dialog" + aria-modal="true", so it is picked up with no edit. */
  function isModal(el) {
    return el.matches('[role="dialog"][aria-modal="true"], [data-elio-dialog]');
  }

  /* A dismissible is a non-modal panel: the header cart push, sliver, and
     anything similar. These must NOT be focus-trapped — trapping a non-modal
     region is worse than leaving it alone. They get Escape only. */
  function isDismissible(el) {
    return el.matches('[data-elio-dismissible]');
  }

  function wireModal(el) {
    if (el.dataset[WIRED]) return;
    el.dataset[WIRED] = 'modal';

    var opener = document.activeElement;
    var closed = false;

    function teardown() {
      if (closed) return;
      closed = true;
      document.removeEventListener('keydown', onKey, true);
      obs.disconnect();
      release(opener && document.contains(opener) ? opener : null);
    }

    function onKey(e) {
      if (e.key !== 'Escape' && e.keyCode !== 27) return;
      var closer = el.querySelector('[data-close], [data-elio-close], .js-drawer-btn-close');
      if (closer) { closer.click(); } else { el.remove(); }
      teardown();
    }

    document.addEventListener('keydown', onKey, true);

    // Watch for the overlay being torn out of the DOM by its own close handler.
    var obs = new MutationObserver(function () {
      if (!document.body.contains(el)) teardown();
    });
    obs.observe(document.body, { childList: true, subtree: true });

    // Let the overlay finish painting before moving focus into it.
    requestAnimationFrame(function () {
      if (!document.body.contains(el)) return;
      if (!trap(el)) {
        var first = focusables(el)[0];
        if (first) first.focus();
      }
    });
  }

  function wireDismissible(el) {
    if (el.dataset[WIRED]) return;
    el.dataset[WIRED] = 'dismissible';

    function onKey(e) {
      if (e.key !== 'Escape' && e.keyCode !== 27) return;
      if (!document.body.contains(el)) {
        document.removeEventListener('keydown', onKey, true);
        return;
      }
      // Only act when the panel is actually on screen.
      if (el.offsetParent === null) return;
      var closer = el.querySelector('[data-close], [data-elio-close], .elio-cp__x, .elio-cp__sliver-x');
      if (closer) closer.click();
    }

    document.addEventListener('keydown', onKey, true);
  }

  function scan(node) {
    if (node.nodeType !== 1) return;
    if (isModal(node)) wireModal(node);
    else if (isDismissible(node)) wireDismissible(node);

    var nested = node.querySelectorAll(
      '[role="dialog"][aria-modal="true"], [data-elio-dialog], [data-elio-dismissible]'
    );
    for (var i = 0; i < nested.length; i++) {
      if (isModal(nested[i])) wireModal(nested[i]);
      else wireDismissible(nested[i]);
    }
  }

  var registry = new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) scan(added[j]);
    }
  });

  function start() {
    scan(document.body);
    registry.observe(document.body, { childList: true, subtree: true });
    liveRegion();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* --- public API ---------------------------------------------------------- */
  window.elioA11y = {
    announce: announce,
    trap: trap,
    release: release,
    /* Call after injecting markup outside the observer's reach. */
    scan: function (root) { scan(root || document.body); }
  };
})();
